import { useEffect, useState } from 'react';
import { absenceApi } from '../../../api/absence.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Balances — per-plan current balance for an employee (derived from the ledger)
 * with an `absence-balance-<planId>` cell, plus the full ledger history below.
 * An admin can post a manual signed adjustment.
 */
export default function BalancesPage() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [balances, setBalances] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    setLoading(true);
    hrApi
      .listEmployees()
      .catch(() => ({ items: [] }))
      .then((e) => setEmployees(e?.items || e || []))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  async function load(empId) {
    if (!empId) {
      setBalances([]);
      setLedger([]);
      return;
    }
    setLoading(true);
    try {
      const [b, l] = await Promise.all([
        absenceApi.balances(empId),
        absenceApi.listLedger({ employee: empId }),
      ]);
      setBalances(b?.items || []);
      setLedger(l?.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load balances');
    } finally {
      setLoading(false);
    }
  }

  function onSelectEmployee(id) {
    setEmployeeId(id);
    setNotice(null);
    load(id);
  }

  async function adjust(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    if (!employeeId) {
      setError('Select an employee first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await absenceApi.addAdjustment({
        employeeId,
        absencePlanId: fd.get('absencePlanId'),
        amount: Number(fd.get('amount')),
        notes: fd.get('notes') || null,
      });
      e.target.reset();
      setNotice('Adjustment recorded');
      load(employeeId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record adjustment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="absence-balances-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Balances</h1>
          <p className="muted">Current balance per plan + ledger history</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="absence-balances-error">{error}</div>}
      {notice && <div className="alert info" data-testid="absence-balances-notice">{notice}</div>}

      <div className="card">
        <label>Employee
          <select data-testid="absence-balance-employee-input" value={employeeId} onChange={(e) => onSelectEmployee(e.target.value)}>
            <option value="">— select —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
      </div>

      <div className="card-grid" data-testid="absence-balance-list" data-loading={loading ? 'true' : 'false'}>
        {balances.map((b) => (
          <div key={b.planId} className="card mini" data-testid={`absence-balance-card-${b.planId}`}>
            <h3>{b.name}</h3>
            <p className="badge" data-testid={`absence-balance-${b.planId}`}>{Number(b.balance)} {b.unit}</p>
            {b.statutory && <span className="badge">statutory</span>}
            {!b.paid && <span className="badge warn">unpaid</span>}
          </div>
        ))}
        {!loading && employeeId && balances.length === 0 && <p className="muted">No active plans.</p>}
        {!employeeId && <p className="muted">Select an employee to see balances.</p>}
      </div>

      {employeeId && (
        <form onSubmit={adjust} className="card form-card grid-form">
          <h3 className="full">Manual adjustment</h3>
          <label>Plan
            <select name="absencePlanId" data-testid="absence-adjust-plan-input" required defaultValue="">
              <option value="" disabled>— select plan —</option>
              {balances.map((b) => <option key={b.planId} value={b.planId}>{b.name}</option>)}
            </select>
          </label>
          <label>Amount (signed)<input name="amount" type="number" step="0.01" data-testid="absence-adjust-amount-input" required /></label>
          <label>Notes<input name="notes" data-testid="absence-adjust-notes-input" /></label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="absence-adjust-submit" disabled={busy}>Record adjustment</button></div>
        </form>
      )}

      <div className="card">
        <h3>Ledger history</h3>
        <table className="data-table" data-testid="absence-ledger-list">
          <thead><tr><th>Date</th><th>Plan</th><th>Type</th><th>Amount</th><th>Balance after</th><th>Notes</th></tr></thead>
          <tbody>
            {ledger.map((l) => (
              <tr key={l.id} data-testid={`absence-ledger-row-${l.id}`}>
                <td>{l.date ? String(l.date).slice(0, 10) : '—'}</td>
                <td>{l.plan_name}</td>
                <td>{l.type}</td>
                <td>{Number(l.amount)}</td>
                <td>{l.balance_after == null ? '—' : Number(l.balance_after)}</td>
                <td>{l.notes || '—'}</td>
              </tr>
            ))}
            {!loading && employeeId && ledger.length === 0 && <tr><td colSpan={6} className="muted">No ledger entries.</td></tr>}
            {!employeeId && <tr><td colSpan={6} className="muted">Select an employee.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
